import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useMaintenance } from '@/features/maintenance/hooks/useMaintenance';
import { MaintenanceScreen } from '@/features/maintenance/components/MaintenanceScreen';
import { routePaths } from '../routes';

/** Lower-case, no trailing slash — the router also matches `/Auth/Callback/`. */
function normalizePath(pathname: string): string {
  return pathname.toLowerCase().replace(/\/+$/, '');
}

/**
 * OAuth hand-off routes are never gated: unmounting them mid-flow would drop
 * the PKCE exchange / ticket forwarding. Once they navigate into the app the
 * gate applies as usual. Kept and compared in normalized form so the exemption
 * matches every spelling the router accepts.
 */
const UNGATED_PATHS: readonly string[] = [routePaths.authCallback, routePaths.authStart].map(
  normalizePath,
);

/**
 * Whole-app maintenance gate. Wraps the router: while the `all` section is
 * active nothing else renders (not even login). Fails open — an unreachable
 * status endpoint never blocks the app.
 */
export function MaintenanceGate({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const { all, refetch, isFetching } = useMaintenance();

  if (all.active && !UNGATED_PATHS.includes(normalizePath(pathname))) {
    return <MaintenanceScreen section={all} onRetry={() => void refetch()} retrying={isFetching} />;
  }

  return children;
}
